'use client';

import { createContext, useContext, useEffect, useState, useCallback, type ReactNode } from 'react';
import { auth } from '@/lib/api';
import type { AuthUser, LoginCredentials, SignupData } from '@/types';

interface AuthContextType {
  user: AuthUser | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (credentials: LoginCredentials) => Promise<void>;
  signup: (data: SignupData) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const refreshUser = useCallback(async () => {
    try {
      const [profile, roleData] = await Promise.all([
        auth.getProfile(),
        auth.getRole(),
      ]);
      
      setUser({
        ...profile,
        role: roleData.role,
      });
    } catch (error) {
      console.error('Failed to refresh user:', error);
      setUser(null);
    }
  }, []);

  // Initialize auth state on mount
  useEffect(() => {
    const initAuth = async () => {
      const token = typeof window !== 'undefined' 
        ? localStorage.getItem('stellar_token') 
        : null;

      if (token) {
        try {
          await refreshUser();
        } catch {
          // Token invalid, clear it
          localStorage.removeItem('stellar_token');
        }
      }
      
      setIsLoading(false);
    };

    initAuth();
  }, [refreshUser]);

  const login = async (credentials: LoginCredentials) => {
    const response = await auth.login(credentials);
    setUser(response.user);
  };

  const signup = async (data: SignupData) => {
    const response = await auth.signup(data);
    setUser(response.user);
  };

  const logout = async () => {
    try {
      await auth.logout();
    } finally {
      setUser(null);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        isAuthenticated: !!user,
        login,
        signup,
        logout,
        refreshUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default AuthContext;
